<template>
  <div class="ds-page" v-if="!$store.getters.isForbidden">
    <div class="page-header d-print-none">
      <div class="row align-items-center">
        <div class="col">
          <div class="page-pretitle">Claims</div>
          <h2 class="page-title">{{ $route.name }} Management</h2>
          <p class="page-subtitle">
            Track, audit, and verify user campaign conversion logs, payout stages, and voucher
            redemption tracking.
          </p>
        </div>
        <div class="col-auto ms-auto">
          <div class="cc-page__header-actions">
            <button
              class="ds-btn"
              :class="showSearch ? 'ds-btn--primary' : 'ds-btn--secondary'"
              @click="showSearch = !showSearch"
            >
              <font-awesome-icon icon="search" />
              {{ showSearch ? 'Hide Filters' : 'Show Filters' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="ds-card" v-if="showSearch">
      <div class="cc-page__table-head">
        <div>
          <div class="cc-page__table-title">Date Range Filter</div>
          <div class="cc-page__table-subtitle">
            Filter distributed claims timeline based on specific creation range records.
          </div>
        </div>
      </div>

      <div class="cc-page__filter-grid rf-page__filter-layout-claims">
        <div class="ds-field">
          <label class="ds-field__label">Referral Date Range</label>
          <el-date-picker
            v-model="search.date"
            type="daterange"
            size="small"
            :picker-options="datePickerOptions"
            class="w-10"
            range-separator="To"
            start-placeholder="Start date"
            end-placeholder="End date"
          />
        </div>

        <div class="cc-page__filter-actions rf-page__filter-actions">
          <button class="ds-btn ds-btn--primary ds-btn--sm" @click="searchHandler()">
            <font-awesome-icon icon="search" /> Search
          </button>
          <button class="ds-btn ds-btn--primary ds-btn--sm" @click="getList('download')">
            <font-awesome-icon :icon="['fas', 'download']" /> Download
          </button>
          <button
            v-if="selectedIds.length > 0"
            class="ds-btn ds-btn--blue"
            @click="handleBatchAction('')"
            :disabled="isBatchActionDisabled"
          >
            Claim
          </button>
        </div>
      </div>
    </div>

    <div class="ds-card">
      <div class="ds-table-wrap">
        <table class="ds-table">
          <thead>
            <tr>
              <th style="width: 40px; text-align: center">
                <input type="checkbox" :checked="isAllSelected" @change="toggleSelectAll" />
              </th>
              <th>ID</th>
              <th>Category Name</th>
              <th>Code Owner</th>
              <th>Code Reference</th>
              <th>Referrer Name</th>
              <th>Referred Name</th>
              <th>Created At</th>
              <th>Updated At</th>
              <th>Status</th>
              <th>Claim Status</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, i) in list"
              :key="i"
              :class="{
                'row-selected': selectedIds.includes(item?.id),
                'row-disabled': !(
                  Number(item?.status) === 1 &&
                  (Number(item?.claim_status) === 0 || Number(item?.claim_status) === 4)
                ),
              }"
              :disabled="
                !(
                  Number(item?.status) === 1 &&
                  (Number(item?.claim_status) === 0 || Number(item?.claim_status) === 4)
                )
              "
            >
              <td style="text-align: center">
                <input
                  type="checkbox"
                  :value="item?.id"
                  v-model="selectedIds"
                  :disabled="
                    !(
                      Number(item?.status) === 1 &&
                      (Number(item?.claim_status) === 0 || Number(item?.claim_status) === 4)
                    )
                  "
                />
              </td>
              <td>
                <span class="cc-page__identity-sub">#{{ item?.id }}</span>
              </td>

              <td>
                <div class="cc-page__identity">
                  <span class="cc-page__identity-name">{{
                    item?.referral_code?.referral_program?.name || '—'
                  }}</span>
                </div>
              </td>

              <td>
                <span class="text-dark font-weight-medium">{{
                  item?.referral_code?.owned_by || '—'
                }}</span>
              </td>

              <td>
                <div class="cc-page__identity">
                  <span
                    class="badge bg-light text-success font-monospace px-2 py-1"
                    style="letter-spacing: 0.5px"
                  >
                    {{ item?.referral_code?.code || '—' }}
                  </span>
                  <span class="text-center cc-page__identity-sub mt-1"
                    >Ref ID: {{ item?.referral_code_id || '—' }}</span
                  >
                </div>
              </td>

              <td>
                <div class="cc-page__identity">
                  <span
                    class="badge bg-light text-primary font-monospace px-2 py-1"
                    style="letter-spacing: 0.5px"
                  >
                    {{ item?.referral_code?.referrer?.full_name || '—' }}
                  </span>
                  <span class="text-center cc-page__identity-sub mt-1">{{
                    item?.referral_code?.referrer?.company_name || '—'
                  }}</span>
                </div>
              </td>

              <td>
                <div class="cc-page__identity">
                  <span
                    class="badge bg-light text-primary font-monospace px-2 py-1"
                    style="letter-spacing: 0.5px"
                  >
                    {{ item?.referred?.full_name || '—' }}
                  </span>
                  <span class="text-center cc-page__identity-sub mt-1">{{
                    item?.referred?.company_name || '—'
                  }}</span>
                </div>
              </td>

              <td>
                <span class="text-muted" style="font-size: 13px">{{
                  item?.created_at_str || '—'
                }}</span>
              </td>
              <td>
                <span class="text-muted" style="font-size: 13px">{{
                  item?.updated_at_str || '—'
                }}</span>
              </td>

              <td>
                <span class="ds-pill" :class="item.status_color">
                  {{ item.status_str }}
                </span>
              </td>

              <td>
                <span class="ds-pill" :class="item.claim_status_color">
                  {{ item.claim_status_str }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <el-empty
          v-if="total_rows === 0 || !list.length"
          description="No referral claims found"
          :image="emptyStateImage"
          class="ds-empty"
        />
      </div>

      <pagination
        :page="page"
        :per_page="per_page"
        :list="list"
        :total_rows="total_rows"
        @pageChange="pageChangeHandler"
      />
    </div>
  </div>
</template>
<script>
import moment from 'moment';
import { upperFirst } from 'lodash';
import emptyStateImage from '@/assets/images/empty-state.png';
import Pagination from '../components/Pagination.vue';
import { GET_REFERRAL_CLAIMS, CLAIM_CODE_ACTION } from '../../store/modules/app_user';
export default {
  components: { Pagination },
  name: 'Referral',
  metaInfo: {
    title: 'Referral Claims',
  },
  data() {
    return {
      showDetailID: false,
      selectedIds: [],
      user_id: null,
      showSearch: true,
      emptyStateImage,
      per_page: 10,
      total_rows: 0,
      page: 1,
      list: [],
      isLoading: true,
      loading_detail: false,
      loader: null,
      search_keyword: '',
      datePickerOptions: {},
      selected_transaction: {},
      loadingButton: false,
      search: {
        date: [moment().subtract(30, 'day').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
        start_date: moment().subtract(30, 'day').format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
      },
    };
  },
  async mounted() {
    const loader = this.$loading.show();
    this.page = 1;
    this.search.start_date = moment(this.search.date[0]).format('YYYY-MM-DD');
    this.search.end_date = moment(this.search.date[1]).format('YYYY-MM-DD');
    await this.getList();
    loader.hide();
  },
  computed: {
    isBatchActionDisabled() {
      const selectedClaims = this.list.filter((item) => this.selectedIds.includes(item.id));
      if (selectedClaims.length === 0) return true;
      return selectedClaims.some((item) => {
        const isStatusSuccess = Number(item.status) === 1;
        const isClaimSubmittedOrHold =
          Number(item.claim_status) === 0 || Number(item.claim_status) === 4;
        const isClaimAllowed = isStatusSuccess && isClaimSubmittedOrHold;
        return !isClaimAllowed;
      });
    },

    selectableClaims() {
      return this.list.filter(
        (item) => Number(item.claim_status) !== 1 && Number(item.claim_status) !== 2
      );
    },

    isAllSelected() {
      if (this.selectableClaims.length === 0) return false;
      return this.selectableClaims.every((item) => this.selectedIds.includes(item.id));
    },
    showingFrom() {
      return this.total_rows ? (this.page - 1) * this.per_page + 1 : 0;
    },
    showingUntil() {
      if (this.total_rows) {
        if (this.list.length < this.per_page) {
          return this.total_rows;
        }
        return this.page * this.per_page;
      }
      return 0;
    },
  },
  methods: {
    toggleSelectAll(event) {
      if (event.target.checked) {
        const selectableIds = this.selectableClaims.map((item) => item.id);
        this.selectedIds = [...new Set([...this.selectedIds, ...selectableIds])];
      } else {
        const selectableIds = this.selectableClaims.map((item) => item.id);
        this.selectedIds = this.selectedIds.filter((id) => !selectableIds.includes(id));
      }
    },

    handleBatchAction(actionType) {
      this.openAdminActionModal(this.selectedIds, actionType);
    },

    openAdminActionModal(ids, defaultAction) {
      const initialAction = defaultAction || 'approve';
      const modalHtmlContent = `
    <div class="admin-modal-layout">
      <p style="margin-bottom: 12px; font-size: 14px;">
        Processing selected claim(s) (IDs: <strong>${
          Array.isArray(ids) ? ids.join(', ') : ids
        }</strong>)
      </p>

      <div style="margin-bottom: 15px;">
        <label style="display: block; margin-bottom: 5px; font-weight: bold; font-size: 13px;">Select Action:</label>
        <select
          id="batch-modal-select-action"
          class="el-input__inner"
          style="width: 100%; height: 32px; padding: 0 10px; font-size: 13px;"
        >
          <!-- Opsi dropdown disesuaikan dengan key objek ADMIN_ACTIONS di backend -->
          <option value="approve" ${initialAction === 'approve' ? 'selected' : ''}>Approve</option>
          <option value="reject" ${initialAction === 'reject' ? 'selected' : ''}>Reject</option>
          <option value="hold" ${initialAction === 'hold' ? 'selected' : ''}>Hold</option>
          <option value="unhold" ${initialAction === 'unhold' ? 'selected' : ''}>Unhold </option>
        </select>
      </div>

      <div>
        <label style="display: block; margin-bottom: 5px; font-weight: bold; font-size: 13px;">Note :</label>
        <textarea
          id="batch-modal-text-note"
          class="el-textarea__inner"
          rows="3"
          placeholder="Type your audit trail note here..."
          style="width: 100%; font-family: inherit; box-sizing: border-box;"
        ></textarea>
      </div>
    </div>
  `;

      this.$msgbox({
        title: 'Action Confirmation',
        message: modalHtmlContent,
        dangerouslyUseHTMLString: true,
        confirmButtonText: 'Submit',
        cancelButtonText: 'Cancel',
        showCancelButton: true,

        beforeClose: async (action, instance, done) => {
          if (action === 'confirm') {
            const selectEl = document.getElementById('batch-modal-select-action');
            const noteEl = document.getElementById('batch-modal-text-note');

            const finalAction = selectEl ? selectEl.value : initialAction;
            const finalNote = noteEl ? noteEl.value : '';

            if (!finalNote || finalNote.trim() === '') {
              this.$message.warning('A note is required for the confirmation.');
              return;
            }

            instance.confirmButtonLoading = true;
            try {
              const formattedIds = Array.isArray(ids) ? ids.map((id) => Number(id)) : [Number(ids)];
              const payload = {
                data: {
                  ids: formattedIds,
                  action: finalAction, // 'approve', 'hold', 'unhold', atau 'reject' dri be
                  note: finalNote,
                },
              };
              await this.$store
                .dispatch(CLAIM_CODE_ACTION, payload)
                .then(() => {
                  this.$message({
                    title: 'Success',
                    type: 'success',
                    message: `Successfully processed batch action: ${finalAction.toUpperCase()}`,
                  });

                  this.selectedIds = [];
                  this.getList();
                  done();
                })
                .catch((err) => {
                  if (typeof popupErrorMessages === 'function') {
                    popupErrorMessages(err.response?.data);
                  } else {
                    this.$message.error(err.response?.data?.message || 'Something went wrong');
                  }
                });
            } catch (err) {
              console.error(err);
            } finally {
              instance.confirmButtonLoading = false;
            }
          } else {
            done();
          }
        },
      }).catch(() => {});
    },
    async getList(mode = 'search') {
      this.isLoading = true;
      const payload = {
        page: this.page,
        per_page: this.per_page,
        start_date: this.search.start_date,
        end_date: this.search.end_date,
      };
      if (mode === 'download') {
        payload.action = 'download';
      }
      await this.$store
        .dispatch(GET_REFERRAL_CLAIMS, payload)
        .then((result) => {
          if (mode === 'download') {
            const contentType =
              result && result.headers ? result.headers['content-type'] : 'text/csv';
            const rawCsvData = result.data ? result.data : result;
            const anchor = document.createElement('a');
            anchor.href = `data:${contentType},${encodeURIComponent(rawCsvData)}`;
            anchor.target = '_blank';
            anchor.download = `Referral-Claims-Report-${this.search.start_date || 'all'}-${
              this.search.end_date || 'all'
            }.csv`;
            anchor.click();
          } else {
            const { rows, count } = this.$store.getters.get_referral_claims;
            this.total_rows = count || 0;
            this.list = rows;
          }
        })
        .catch((error) => {});

      this.isLoading = false;
    },
    pageChangeHandler(page) {
      this.page = page;
      this.getList();
    },
    searchHandler() {
      this.page = 1;
      this.search.start_date = moment(this.search.date[0]).format('YYYY-MM-DD');
      this.search.end_date = moment(this.search.date[1]).format('YYYY-MM-DD');
      this.getList();
    },
  },
};
</script>
<style>
.el-range-separator {
  width: 10% !important;
}

.child1-sticky {
  position: sticky;
  right: 0px;
  width: 40px;
  background: #fff;
}

.child2-sticky {
  position: sticky;
  right: 69px;
  width: 255px;
  background: #fff;
}
/* del */
.table-container {
  width: 100%;
  overflow-x: auto;
}

.table-responsive {
  overflow-x: auto;
}

.buttonload {
  background-color: #006ea5; /* Green background */
  border: none; /* Remove borders */
  color: white; /* White text */
  padding: 12px 16px; /* Some padding */
  font-size: 16px; /* Set a font size */
}

.dialog-footer button:first-child {
  margin-right: 10px;
}

.rf-page__filter-layout-claims {
  grid-template-columns: minmax(320px, 2fr) auto !important;
}

.rf-page__filter-actions {
  align-self: end;
}

.font-weight-medium {
  font-weight: 500;
}

.font-monospace {
  font-family: Fira Code, Monaco, Consolas, monospace;
  font-size: 12px;
}

@media (max-width: 768px) {
  .rf-page__filter-layout-claims {
    grid-template-columns: 1fr !important;
  }
  .rf-page__filter-actions {
    justify-content: flex-start;
  }
}

.cc-page__header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cc-page__table-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 6px;
}

.cc-page__filter-card {
  margin-bottom: 14px;
}

.cc-page__filter-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr auto;
  gap: 12px;
  padding: 8px 20px 18px;
  align-items: end;
}

.cc-page__filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.cc-page__table-title {
  font-size: 15px;
  font-weight: 700;
  color: #182433;
}

.cc-page__table-subtitle {
  font-size: 12px;
  color: #656d77;
  margin-top: 4px;
}

.cc-page__identity {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cc-page__identity-name {
  font-weight: 700;
  color: #182433;
}

.cc-page__identity-sub {
  font-size: 12px;
  color: #656d77;
}

.cc-page__action-trigger {
  min-width: 108px;
  justify-content: center;
}

.cc-page__pks-cell {
  display: inline-flex;
  flex-direction: column;
  gap: 6px;
}

.cc-page__pks-pill {
  width: fit-content;
}

.cc-page__action-menu {
  padding: 8px;
}

.cc-page__action-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 190px;
  color: #1f2937;
}

.cc-page__action-item--danger {
  color: #c0392b;
}

.cc-page__action-icon {
  width: 18px;
  text-align: center;
}

.company-client-modal {
  padding: 6px 2px 2px;
}

.company-client-modal__intro {
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(32, 107, 196, 0.08);
  color: #304152;
  font-size: 13px;
}

.company-client-modal__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.company-client-modal__field-full {
  grid-column: 1 / -1;
}

.company-client-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}

@media (max-width: 900px) {
  .cc-page__filter-grid {
    grid-template-columns: 1fr;
  }

  .cc-page__filter-actions {
    justify-content: flex-start;
  }

  .company-client-modal__grid {
    grid-template-columns: 1fr;
  }
}

/* .row-disabled {
  opacity: 0.6;
  background-color: #f9f9f9;
  cursor: not-allowed;
} */
</style>
